import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge } from '@mono/ui-components';
import { ChevronDown, ChevronRight, FileText, Layers, Search, Settings, X } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
function formatCurrency(value) {
    if (value === 0) return '';
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
    return `$${value.toFixed(0)}`;
}
/** Detect SLIN parent: 0002AA → 0002, 0002AB → 0002 */ function findParent(clinNumber, allClinNumbers) {
    for(let len = clinNumber.length - 1; len >= 4; len--){
        const prefix = clinNumber.slice(0, len);
        if (allClinNumbers.has(prefix) && prefix !== clinNumber) return prefix;
    }
    return null;
}
function matchesFilter(clin, filters) {
    // Search query — fuzzy match on clinNumber or title
    if (filters.searchQuery) {
        const q = filters.searchQuery.toLowerCase();
        const matchNumber = clin.clinNumber.toLowerCase().includes(q);
        const matchTitle = clin.metadata.title?.toLowerCase().includes(q);
        if (!matchNumber && !matchTitle) return false;
    }
    // Hide NSP if toggled off
    if (!filters.showNsp && clin.metadata.isNsp) return false;
    // Labor only
    if (filters.laborOnly && clin.laborStructure.type === 'none') return false;
    // Optional tasks only
    if (filters.optionalTasksOnly && !clin.metadata.isOptionalTask) return false;
    // Needs pricing (zero total or missing rates)
    if (filters.needsPricingOnly && clin.grandTotal > 0) return false;
    return true;
}
function hasActiveFilters(filters) {
    return filters.searchQuery !== '' || !filters.showNsp || filters.laborOnly || filters.optionalTasksOnly || filters.needsPricingOnly;
}
function buildTree(clinPricing, sharedTierConfigs, filters) {
    const nodes = [];
    const isFiltering = hasActiveFilters(filters);
    // Navigation nodes (always shown unless searching)
    if (!filters.searchQuery) {
        nodes.push({
            id: 'overview',
            label: 'Overview',
            type: 'nav'
        });
        nodes.push({
            id: 'rate-card',
            label: 'Rate Card',
            type: 'nav'
        });
        nodes.push({
            id: 'indirect',
            label: 'Indirect Rates',
            type: 'nav'
        });
        // Shared tiers group
        if (sharedTierConfigs.length > 0) {
            const tierChildren = sharedTierConfigs.sort((a, b)=>b.tierNumber - a.tierNumber).map((config)=>{
                const { min, max } = config.fteConstraints;
                const fteLabel = min != null && max != null ? min === max ? `${min} FTEs` : `${min}-${max} FTEs` : '';
                return {
                    id: `shared-tier-${config.id}`,
                    label: config.tierLabel,
                    type: 'shared-tier',
                    annotation: fteLabel
                };
            });
            nodes.push({
                id: 'shared-tiers-group',
                label: 'Shared Tiers',
                type: 'group',
                children: tierChildren
            });
        }
    }
    // Filter CLINs
    const filteredClins = isFiltering ? clinPricing.filter((c)=>matchesFilter(c, filters)) : clinPricing;
    // Group CLINs by period
    const allClinNumbers = new Set(clinPricing.map((c)=>c.clinNumber));
    const periodMap = new Map();
    const optionalTasks = [];
    for (const clin of filteredClins){
        if (clin.metadata.isOptionalTask) {
            optionalTasks.push(clin);
            continue;
        }
        const period = clin.metadata.periodName || 'Unassigned';
        const existing = periodMap.get(period) ?? [];
        existing.push(clin);
        periodMap.set(period, existing);
    }
    // Build period groups with SLIN nesting
    for (const [period, clins] of periodMap){
        const clinNodes = [];
        const childMap = new Map();
        // First pass: identify SLINs
        for (const clin of clins){
            const parent = findParent(clin.clinNumber, allClinNumbers);
            if (parent) {
                const children = childMap.get(parent) ?? [];
                children.push({
                    id: clin.clinNumber,
                    label: `${clin.clinNumber}`,
                    type: 'slin',
                    clinPricing: clin,
                    badge: clin.metadata.contractType,
                    total: clin.grandTotal,
                    annotation: clin.metadata.title,
                    isNsp: clin.metadata.isNsp
                });
                childMap.set(parent, children);
            }
        }
        // Second pass: build parent nodes
        for (const clin of clins){
            const parent = findParent(clin.clinNumber, allClinNumbers);
            if (parent) continue; // Skip SLINs, they're nested
            const children = childMap.get(clin.clinNumber);
            clinNodes.push({
                id: clin.clinNumber,
                label: `${clin.clinNumber}`,
                type: 'clin',
                clinPricing: clin,
                badge: clin.metadata.contractType,
                total: clin.grandTotal,
                annotation: clin.metadata.title,
                children,
                isNsp: clin.metadata.isNsp
            });
        }
        nodes.push({
            id: `period-${period}`,
            label: period,
            type: 'period',
            children: clinNodes,
            total: clins.reduce((s, c)=>s + c.grandTotal, 0)
        });
    }
    // Optional tasks group
    if (optionalTasks.length > 0) {
        const optChildren = optionalTasks.map((clin)=>({
                id: clin.clinNumber,
                label: `${clin.clinNumber}`,
                type: 'clin',
                clinPricing: clin,
                badge: clin.metadata.contractType,
                total: clin.grandTotal,
                annotation: clin.metadata.title,
                isNsp: clin.metadata.isNsp,
                ...clin.metadata.correspondingBaseClin ? {
                    annotation: `${clin.metadata.title || ''} → ${clin.metadata.correspondingBaseClin}`.trim()
                } : {}
            }));
        nodes.push({
            id: 'optional-tasks',
            label: 'Optional Tasks',
            type: 'group',
            children: optChildren,
            total: optionalTasks.reduce((s, c)=>s + c.grandTotal, 0)
        });
    }
    return nodes;
}
// ── Tree Node Renderer ──────────────────────────────────────────────
function TreeNodeItem({ node, depth, selectedNodeId, onSelect }) {
    const [expanded, setExpanded] = useState(true);
    const hasChildren = node.children && node.children.length > 0;
    const isSelected = selectedNodeId === node.id;
    const isSelectable = node.type !== 'period' && node.type !== 'group';
    const handleClick = useCallback(()=>{
        if (hasChildren && (node.type === 'period' || node.type === 'group')) {
            setExpanded((prev)=>!prev);
        } else if (isSelectable) {
            onSelect(node.id);
        }
    }, [
        hasChildren,
        node.type,
        node.id,
        isSelectable,
        onSelect
    ]);
    const handleClinClick = useCallback(()=>{
        if (isSelectable) {
            onSelect(node.id);
        }
    }, [
        node.id,
        isSelectable,
        onSelect
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: `flex items-center gap-1 px-2 py-1 text-sm cursor-pointer rounded-md mx-1 ${isSelected ? 'bg-blue-100 text-blue-900 font-medium' : 'hover:bg-gray-100 text-gray-700'} ${node.isNsp ? 'opacity-50' : ''}`,
                style: {
                    paddingLeft: `${depth * 12 + 8}px`
                },
                onClick: isSelectable ? handleClinClick : handleClick,
                children: [
                    hasChildren && (node.type === 'period' || node.type === 'group' || node.type === 'clin') ? /*#__PURE__*/ _jsx("button", {
                        onClick: (e)=>{
                            e.stopPropagation();
                            setExpanded((prev)=>!prev);
                        },
                        className: "flex-shrink-0 w-4 h-4 flex items-center justify-center",
                        children: expanded ? /*#__PURE__*/ _jsx(ChevronDown, {
                            className: "w-3.5 h-3.5 text-gray-400"
                        }) : /*#__PURE__*/ _jsx(ChevronRight, {
                            className: "w-3.5 h-3.5 text-gray-400"
                        })
                    }) : /*#__PURE__*/ _jsx("span", {
                        className: "w-4 flex-shrink-0"
                    }),
                    node.type === 'nav' && node.id === 'overview' && /*#__PURE__*/ _jsx(Layers, {
                        className: "w-3.5 h-3.5 text-gray-400 flex-shrink-0"
                    }),
                    node.type === 'nav' && node.id !== 'overview' && /*#__PURE__*/ _jsx(Settings, {
                        className: "w-3.5 h-3.5 text-gray-400 flex-shrink-0"
                    }),
                    (node.type === 'clin' || node.type === 'slin') && /*#__PURE__*/ _jsx(FileText, {
                        className: "w-3.5 h-3.5 text-gray-400 flex-shrink-0"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "truncate flex-1 min-w-0",
                        children: node.label
                    }),
                    node.isNsp && /*#__PURE__*/ _jsx(Badge, {
                        variant: "outline",
                        className: "text-[9px] px-1 py-0 flex-shrink-0 leading-tight bg-gray-100 text-gray-500 border-gray-300",
                        children: "NSP"
                    }),
                    node.badge && !node.isNsp && /*#__PURE__*/ _jsx(Badge, {
                        variant: "outline",
                        className: "text-[9px] px-1 py-0 flex-shrink-0 leading-tight",
                        children: node.badge
                    }),
                    node.total != null && node.total > 0 && /*#__PURE__*/ _jsx("span", {
                        className: "text-[10px] text-gray-400 flex-shrink-0 tabular-nums",
                        children: formatCurrency(node.total)
                    })
                ]
            }),
            (node.type === 'clin' || node.type === 'slin') && node.annotation && isSelected && /*#__PURE__*/ _jsx("div", {
                className: "text-[10px] text-gray-400 truncate px-2 pb-0.5",
                style: {
                    paddingLeft: `${depth * 12 + 32}px`
                },
                title: node.annotation,
                children: node.annotation
            }),
            hasChildren && expanded && node.children?.map((child)=>/*#__PURE__*/ _jsx(TreeNodeItem, {
                    node: child,
                    depth: depth + 1,
                    selectedNodeId: selectedNodeId,
                    onSelect: onSelect
                }, child.id))
        ]
    });
}
// ── Filter Bar ──────────────────────────────────────────────────────
function FilterBar({ filters, onChange, clinCount, filteredCount }) {
    const active = hasActiveFilters(filters);
    return /*#__PURE__*/ _jsxs("div", {
        className: "px-2 pb-2 space-y-1.5 border-b border-gray-200",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "relative",
                children: [
                    /*#__PURE__*/ _jsx(Search, {
                        className: "absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
                    }),
                    /*#__PURE__*/ _jsx("input", {
                        type: "text",
                        value: filters.searchQuery,
                        onChange: (e)=>onChange({
                                ...filters,
                                searchQuery: e.target.value
                            }),
                        placeholder: "Search CLINs...",
                        className: "w-full pl-7 pr-7 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                    }),
                    filters.searchQuery && /*#__PURE__*/ _jsx("button", {
                        onClick: ()=>onChange({
                                ...filters,
                                searchQuery: ''
                            }),
                        className: "absolute right-2 top-1/2 -translate-y-1/2",
                        children: /*#__PURE__*/ _jsx(X, {
                            className: "w-3 h-3 text-gray-400 hover:text-gray-600"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap gap-1",
                children: [
                    /*#__PURE__*/ _jsx(FilterChip, {
                        label: "Labor Only",
                        active: filters.laborOnly,
                        onClick: ()=>onChange({
                                ...filters,
                                laborOnly: !filters.laborOnly
                            })
                    }),
                    /*#__PURE__*/ _jsx(FilterChip, {
                        label: "Optional",
                        active: filters.optionalTasksOnly,
                        onClick: ()=>onChange({
                                ...filters,
                                optionalTasksOnly: !filters.optionalTasksOnly
                            })
                    }),
                    /*#__PURE__*/ _jsx(FilterChip, {
                        label: "Needs Pricing",
                        active: filters.needsPricingOnly,
                        onClick: ()=>onChange({
                                ...filters,
                                needsPricingOnly: !filters.needsPricingOnly
                            })
                    }),
                    /*#__PURE__*/ _jsx(FilterChip, {
                        label: "Hide NSP",
                        active: !filters.showNsp,
                        onClick: ()=>onChange({
                                ...filters,
                                showNsp: !filters.showNsp
                            })
                    })
                ]
            }),
            active && /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between text-[10px] text-gray-400",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        children: [
                            filteredCount,
                            " of ",
                            clinCount,
                            " CLINs"
                        ]
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        onClick: ()=>onChange({
                                searchQuery: '',
                                showNsp: true,
                                laborOnly: false,
                                optionalTasksOnly: false,
                                needsPricingOnly: false
                            }),
                        className: "text-blue-500 hover:text-blue-600",
                        children: "Clear"
                    })
                ]
            })
        ]
    });
}
function FilterChip({ label, active, onClick }) {
    return /*#__PURE__*/ _jsx("button", {
        onClick: onClick,
        className: `text-[10px] px-1.5 py-0.5 rounded-full border transition-colors ${active ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`,
        children: label
    });
}
export const ProposalTree = /*#__PURE__*/ memo(function ProposalTree({ pricing: _pricing, clinPricing, sharedTierConfigs, selectedNodeId, onSelect }) {
    const [filters, setFilters] = useState({
        searchQuery: '',
        showNsp: true,
        laborOnly: false,
        optionalTasksOnly: false,
        needsPricingOnly: false
    });
    const filteredCount = useMemo(()=>clinPricing.filter((c)=>matchesFilter(c, filters)).length, [
        clinPricing,
        filters
    ]);
    const tree = useMemo(()=>buildTree(clinPricing, sharedTierConfigs, filters), [
        clinPricing,
        sharedTierConfigs,
        filters
    ]);
    return /*#__PURE__*/ _jsxs("nav", {
        className: "py-2 text-sm",
        children: [
            /*#__PURE__*/ _jsx(FilterBar, {
                filters: filters,
                onChange: setFilters,
                clinCount: clinPricing.length,
                filteredCount: filteredCount
            }),
            tree.map((node)=>/*#__PURE__*/ _jsx(TreeNodeItem, {
                    node: node,
                    depth: 0,
                    selectedNodeId: selectedNodeId,
                    onSelect: onSelect
                }, node.id))
        ]
    });
});
